import React, { useEffect, useMemo, useState } from "react";
import * as S from "./PageWrapper.style";
import { TryForFreeBanner } from "./TryForFreeBanner";
import { Notifications } from "~/components/notifications";
import { useMediaQuery } from "~/hooks/useMediaQuery";
import { PlansModal } from "~/components/plans/plansModal";
import { ResumeModal } from "~/components/resumeModal";
import { LimitsModal } from "../limits-modal";
import { useAccount } from "~/hooks/useAccount";
import { useClarityIdentify } from "~/hooks/useClarityIdentify";
import { useNavigate } from "react-router-dom";
import { Onboarding } from "../onboarding";
import { DISABLE_PAYMENTS } from "~/helpers/plans";
import { usePlans } from "~/api/billing/queries";
import { recheckBillingStatus } from "~/api/billing/requests";
import { log } from "~/helpers/datadog";
import { paths } from "~/router/paths";
import { IPlan, SubscriptionType } from "~/types/billing";
import { PlanStatus } from "~/types/account";
import { Userpilot } from "userpilot";
import { useLocation } from "react-router-dom";
import { ModalConnectToStore } from "../connect-to-store/modal";
import { ShowUpgradeModal } from "../upgrade-funnel/showUpgradeModal";
import { AnualPlansModal } from "../plans/anualPlansModal";
import { SubscriptionLockModal } from "~/components/subscription-lock-modal";
import { SubscriptionLockReason } from "~/types/account";
import { Terms } from "~/constants/terms";
import { HELP_CENTER_URL } from "~/constants/sidebar";
import { AliDropPromotionModal } from "../alidrop-promotion-modal";
import { FORCE_SIGN_OUT } from "~/helpers/customEvents";
import { useDesktopSidebar } from "~/contexts/DesktopSidebarContext";
import { isInPaymentGrace, clearPaymentGrace, GRACE_DURATION_MS } from "~/helpers/paymentGrace";

interface IProps {
  children: React.ReactNode;
}

// Resolve the plan id the onboarding flow should send the user to checkout for.
// Prefers the Empire monthly "for winning" plan, then Professional monthly, and
// finally falls back to the first active plan so a backend plan-config change can
// never strand users with no checkout target (which is what caused the loop).
const resolveCheckoutPlanId = (
  plansList: IPlan[] | undefined
): string | undefined => {
  const empire = plansList?.find(
    (p) =>
      p.name === "Empire" &&
      p.interval === SubscriptionType.MONTHLY &&
      p.for_winning
  );
  const professional = plansList?.find(
    (p) =>
      p.name === "Professional" &&
      p.interval === SubscriptionType.MONTHLY &&
      p.for_winning
  );
  return (empire ?? professional ?? plansList?.[0])?.id;
};

export const PageWrapper: React.FC<IProps> = ({ children = null }) => {
  const supportedLockReasons: Exclude<SubscriptionLockReason, null>[] = [
    "trial_ended",
    "cancelled_period_ended",
    "subscription_expired",
    "past_due",
    "suspended",
    "billing_status_unknown",
  ];
  const pageLocation = useLocation();
  const { getAccount, account } = useAccount();
  useClarityIdentify();
  const isSettingsPage = pageLocation.pathname.includes("settings");
  const isSettingsPlansPage = pageLocation.pathname === paths.settings.plans;
  const isSettingsAccountPage = pageLocation.pathname === paths.settings.account;
  const isNotMobile = useMediaQuery("tablet");
  const { sidebarWidthPx } = useDesktopSidebar();
  const queryParams = useMemo(() => new URLSearchParams(pageLocation.search), [pageLocation.search]);
  const [isShowingOnboarding, setIsShowingOnboarding] = useState(false);

  const planId = queryParams.get("plan_id");
  const showRating = queryParams.get("show_rating");
  const onboarding = queryParams.get("onboarding");
  const annual = queryParams.get("annual");
  const { data: plans, refetch: refetchPlans } = usePlans();
  const activePlans = plans?.filter(
    (plan: IPlan) => plan.status === PlanStatus.ACTIVE
  );

  const navigate = useNavigate();
  const isDashboardPage = pageLocation.pathname === paths.app.home;
  const subscriptionLockReason = supportedLockReasons.find(
    (reason) => reason === account?.subscription_lock_reason
  );

  // "Try again" used to be a bare window.location.reload(), which re-read the same
  // cached billing state and so could never clear the lock. Ask the API to re-check
  // against Stripe (and retry any outstanding invoice) before refreshing the account.
  // Nothing awaits this (it is an onClick handler), so it must never reject —
  // an unhandled rejection here would leave the user stuck behind the modal with
  // no feedback. Both calls are guarded independently: a failed re-check must
  // still refresh the account, and a failed refresh must not escape.
  const handleRecheckBillingStatus = async () => {
    try {
      await recheckBillingStatus();
    } catch (error) {
      log.error("Failed to re-check billing status", error);
    }

    try {
      await getAccount();
    } catch (error) {
      log.error("Failed to refresh account after billing re-check", error);
    }
  };

  useEffect(() => {
    Userpilot.reload();
  }, [pageLocation]);

  useEffect(() => {
    const handleForceSignOut = () => {
      navigate(paths.auth.login, { replace: true });
    };

    window.addEventListener(FORCE_SIGN_OUT, handleForceSignOut);
    return () => {
      window.removeEventListener(FORCE_SIGN_OUT, handleForceSignOut);
    };
  }, [navigate]);

  useEffect(() => {
    const checkOnboardingCondition = async () => {
      const account = await getAccount();

      // If the account now has an active subscription, the grace period
      // served its purpose — clear it so normal checks resume.
      if (account?.active_subscription) {
        clearPaymentGrace();
      }

      // During the payment grace window the backend may not have processed
      // the webhook yet, so active_subscription can still be null even
      // though the user just paid.  Skip paywall / onboarding redirects
      // while the grace is active to avoid bouncing paid users.
      const graceActive = isInPaymentGrace();

      if (onboarding && account && !account?.active_subscription && !graceActive) {
        setIsShowingOnboarding(true);
      }

      const searchParams = new URLSearchParams(window.location.search);

      if (annual) {
        searchParams.set("annual", "true");
        navigate(`?${searchParams.toString()}`);
      }

      if (
        account &&
        !account?.active_subscription &&
        !account?.has_subscribed_before &&
        !planId &&
        !showRating &&
        !graceActive
      ) {
        searchParams.set("onboarding", "true");
        searchParams.set("origin", account?.signup_origin);
        navigate(`?${searchParams.toString()}`);
      } else if (!!onboarding) {
        setIsShowingOnboarding(false);
        searchParams.delete("onboarding");
        const queryParams = searchParams.toString();
        const newUrl =
          window.location.pathname + (!!queryParams ? `?${queryParams}` : "");
        window.history.replaceState(null, "", newUrl);
      }
    };

    checkOnboardingCondition();

    // If the grace window is active, schedule a re-check so the paywall /
    // onboarding logic re-evaluates once the grace expires.
    if (isInPaymentGrace()) {
      const timer = setTimeout(checkOnboardingCondition, GRACE_DURATION_MS);
      return () => clearTimeout(timer);
    }
  }, [planId, onboarding, annual]);

  return (
    <S.PageWrapper padding={"24px"} $sidebarWidthPx={sidebarWidthPx}>
      <S.PageHeader>{isNotMobile ? <Notifications /> : null}</S.PageHeader>
      <S.PageMaxWidth>
        {isDashboardPage && <TryForFreeBanner />}
        <>{children}</>
      </S.PageMaxWidth>
      <PlansModal />
      <AliDropPromotionModal />
      <AnualPlansModal />
      <ResumeModal />
      <LimitsModal />
      <ShowUpgradeModal />

      <ModalConnectToStore />

      <Onboarding
        isShowing={isShowingOnboarding}
        onFinish={async () => {
          if (DISABLE_PAYMENTS) {
            setIsShowingOnboarding(false);
            return;
          }

          let selectedPlanId = resolveCheckoutPlanId(activePlans);

          // The plans query may not have resolved yet (slow network, or it
          // fired before auth was ready). Refetch once and retry before giving
          // up, so we never navigate to /checkout/undefined.
          if (!selectedPlanId) {
            const { data: freshPlans } = await refetchPlans();
            const freshActivePlans = freshPlans?.filter(
              (plan: IPlan) => plan.status === PlanStatus.ACTIVE
            );
            selectedPlanId = resolveCheckoutPlanId(freshActivePlans);
          }

          // Hard failure: plans genuinely unresolvable. Go to settings/plans
          // once (modal closed) instead of bouncing through /checkout/undefined
          // and re-arming onboarding in a loop.
          if (!selectedPlanId) {
            setIsShowingOnboarding(false);
            navigate(paths.settings.plans);
            return;
          }

          setIsShowingOnboarding(false);
          navigate(paths.checkout.index.replace(":id", String(selectedPlanId)));
        }}
      />
      {subscriptionLockReason && !isSettingsPlansPage && !isSettingsAccountPage && !planId && !isInPaymentGrace() ? (
        <SubscriptionLockModal
          isShowing
          reason={subscriptionLockReason}
          onPrimaryAction={() => {
            if (subscriptionLockReason === "suspended") {
              window.open(HELP_CENTER_URL, "_blank");
            } else if (subscriptionLockReason === "past_due" || subscriptionLockReason === "billing_status_unknown") {
              navigate(paths.settings.account);
            } else {
              navigate(paths.settings.plans);
            }
          }}
          onSecondaryAction={() => {
            if (subscriptionLockReason === "suspended") {
              window.open(Terms.terms, "_blank");
            } else if (subscriptionLockReason === "cancelled_period_ended") {
              window.open(HELP_CENTER_URL, "_blank");
            } else if (subscriptionLockReason === "past_due" || subscriptionLockReason === "billing_status_unknown") {
              handleRecheckBillingStatus();
            } else {
              navigate(paths.settings.plans);
            }
          }}
        />
      ) : null}
    </S.PageWrapper>
  );
};

export const PageWrapperFullScreen: React.FC<IProps> = ({
  children = null,
}) => {
  const pageLocation = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    Userpilot.reload();
  }, [pageLocation]);

  useEffect(() => {
    const handleForceSignOut = () => {
      navigate(paths.auth.login, { replace: true });
    };

    window.addEventListener(FORCE_SIGN_OUT, handleForceSignOut);
    return () => {
      window.removeEventListener(FORCE_SIGN_OUT, handleForceSignOut);
    };
  }, [navigate]);

  return (
    <S.PageWrapperFullscreen>
      <PlansModal />
      <AnualPlansModal />
      <ResumeModal />
      <LimitsModal />
      <>{children}</>
    </S.PageWrapperFullscreen>
  );
};
